<template>
    <v-card elevation="7">
        <v-card-title class="text-subtitle-2 py-1 px-2">{{
            title
        }}</v-card-title>
        <v-card-text class="px-2">
            <v-simple-table
                fixed-header
                height="200px"
                class="scope-options-table"
            >
                <thead>
                    <tr>
                        <th
                            v-for="header in headers"
                            :key="`header-${header.value}`"
                        >
                            {{ header.text }}
                        </th>
                    </tr>
                </thead>
                <tbody
                    tabindex="0"
                    @keydown.up="onKeydownUp"
                    @keydown.down="onKeydownDown"
                >
                    <!-- TODO: implement the dynamic named slots logic here -->
                    <tr
                        v-for="(item, index) in items"
                        :key="`row-${index}`"
                        :class="{
                            'selected-row': selectedItemIndex === index
                        }"
                        @click.capture="onTableRowClick(index)"
                    >
                        <td>
                            <span>
                                {{ item.name }}
                            </span>
                        </td>
                        <td>
                            <v-checkbox
                                v-model="item.withGrant"
                                hide-details
                                class="ma-0"
                                color="accent"
                                :disabled="disableActions || item.disableEdit"
                                aria-label="scope-role-granted"
                                @change="onWithGrantChange"
                            ></v-checkbox>
                        </td>
                        <td>
                            <v-avatar
                                v-if="infoType[index].type === 'info'"
                                size="24"
                            >
                                <span class="text-body-2">{{
                                    infoType[index].text
                                }}</span>
                            </v-avatar>
                            <span v-if="infoType[index].type === 'error'">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-icon color="warning" v-on="on">
                                            {{ icons.warning }}
                                        </v-icon>
                                    </template>
                                    <span>{{ infoType[index].text }}</span>
                                </v-tooltip>
                            </span>
                        </td>
                        <td>
                            <v-btn
                                v-show="!(disableActions || item.disableEdit)"
                                color="accent"
                                x-small
                                text
                                class="text-caption font-weight-medium"
                                :disabled="disableActions || item.disableEdit"
                                @click="onRowActionClick(item)"
                                >remove
                            </v-btn>
                        </td>
                    </tr>
                    <!-- the no data found text -->
                    <tr v-if="!items.length">
                        <td
                            class="text-center warning--text py-5 text-body-2"
                            colspan="4"
                        >
                            {{ noDataText }}
                        </td>
                    </tr>
                </tbody>
            </v-simple-table>
        </v-card-text>
    </v-card>
</template>

<script>
export default {
    name: 'ScopeOptionsTable',
    props: {
        title: {
            type: String,
            required: true
        },
        headers: {
            type: Array,
            required: true
        },
        items: {
            type: Array,
            required: true
        },
        selectedItemIndex: {
            type: Number,
            required: false,
            default: -1
        },
        noDataText: {
            type: String,
            required: false,
            default: 'No data found...'
        },
        parentWithGrant: {
            type: Boolean,
            required: false,
            default: false
        },
        disableActions: {
            type: Boolean,
            required: false,
            default: true
        }
    },
    computed: {
        withGrantCount() {
            return this.items.filter(item => item.withGrant === true).length;
        },
        infoType() {
            return this.items.map(item => {
                if (item.error) {
                    return {
                        type: 'error',
                        text: item.error
                    };
                } else if (this.parentWithGrant && this.withGrantCount <= 0) {
                    return {
                        type: 'error',
                        text:
                            'At least one role with grant is required when the scope is with grant!'
                    };
                } else if (item.info) {
                    return {
                        type: 'info',
                        text: item.info
                    };
                } else {
                    return { type: null, text: null };
                }
            });
        }
    },
    methods: {
        onRowActionClick(item) {
            this.$emit('on-row-action-click', item);
        },
        onTableRowClick(index) {
            if (this.selectedItemIndex !== index) {
                this.$emit('on-table-row-select', index);
            }
        },
        onWithGrantChange(value) {
            // need to set this property at the parent to trigger the computed prop changes.
            this.$emit('on-row-with-grant-change', value);
        },
        onKeydownDown() {
            this.selectNextRow();
        },
        onKeydownUp() {
            this.selectNextRow(true);
        },
        selectNextRow(directionUp) {
            // will make the next row selected...
            // directionUp: direction is up or down.
            this.$nextTick(() => {
                let selectedRow = this.$el.querySelector(
                    'tbody tr[class="selected-row"]'
                );
                let nextRow = selectedRow.nextElementSibling;
                if (directionUp) {
                    nextRow = selectedRow.previousElementSibling;
                }
                if (selectedRow && nextRow) {
                    nextRow.click();
                } else {
                    selectedRow = this.$el.querySelector('tbody tr');
                    if (directionUp) {
                        selectedRow = this.$el.querySelector(
                            'tbody tr:last-child'
                        );
                    }
                    if (selectedRow) {
                        selectedRow.click();
                    }
                }
            });
        }
    }
};
</script>

<style scoped lang="scss">
.scope-options-table {
    tbody {
        outline: none;
    }
    thead {
        th {
            font-size: 0.75rem;
            font-weight: 600;
            padding: 0 8px;
            border-bottom: 2px solid lightgray;
            height: 40px;
        }
    }
    tbody {
        td {
            font-size: 0.75rem;
            font-weight: 400;
            padding: 0 8px;
        }
    }
}
.scope-options-table.theme--dark {
    ::v-deep table:focus-within {
        border-bottom: solid 3px rgba(map-get($sky-blue, 'base'), 0.5);
        margin-bottom: -3px;
    }
    .selected-row {
        background-color: map-get($deep-grey, 'darken-2');
        .v-btn {
            color: map-get($genes-blue, 'lighten-3') !important;
        }
    }
}
.scope-options-table.theme--light {
    ::v-deep table:focus-within {
        border-bottom: solid 3px rgba(map-get($deep-blue, 'base'), 0.3);
        margin-bottom: -3px;
    }
    .selected-row {
        background-color: map-get($deep-grey, 'lighten-5');
    }
}
</style>
